import { Controller } from '@hotwired/stimulus';
import Swal from 'sweetalert2';

interface SavedFilter {
    id: number;
    name: string;
    filters: Record<string, any>;
}

interface SavedOption {
    value: string;
    label: string;
}

export default class extends Controller<HTMLElement> {

    static targets = [
        'select',
    ];

    static values = {
        type: String,
    };

    declare readonly selectTarget: HTMLSelectElement;
    declare readonly hasSelectTarget: boolean;
    declare readonly typeValue: string;

    private savedFilters: SavedFilter[] = [];

    /*
     * ========================================
     * CONNECT
     * ========================================
     */
    connect(): void {

        if (
            !this.typeValue
            ||
            !this.hasSelectTarget
        ) {

            return;
        }

        this.loadFilters();
    }

    /*
     * ========================================
     * LOAD FILTERS
     * ========================================
     */
    async loadFilters(): Promise<void> {

        const response = await fetch(
            `/saved-filter/list/${this.typeValue}`
        );

        this.savedFilters =
            await response.json();

        this.renderOptions();
    }

    /*
     * ========================================
     * RENDER OPTIONS
     * ========================================
     */
    renderOptions(): void {

        this.selectTarget.innerHTML = `
            <option value="">
                Filtros guardados
            </option>
        `;

        this.savedFilters.forEach(filter => {

            const option =
                document.createElement(
                    'option'
                );

            option.value =
                String(filter.id);

            option.textContent =
                filter.name;

            this.selectTarget.appendChild(
                option
            );
        });
    }

    /*
     * ========================================
     * SAVE FILTER
     * ========================================
     */
    async save(): Promise<void> {

        const result =
            await Swal.fire({

                title:
                    'Guardar filtro',

                input: 'text',

                inputLabel:
                    'Nombre del filtro',

                inputPlaceholder:
                    'Ejemplo: rutas sábado',

                showCancelButton: true,

                confirmButtonText:
                    'Guardar',

                cancelButtonText:
                    'Cancelar',
            });

        if (
            !result.isConfirmed
            ||
            !result.value
        ) {

            return;
        }

        const filters =
            this.collectFilters();

        Swal.fire({

            title:
                'Guardando...',

            text:
                'Por favor espera',

            allowOutsideClick:
                false,

            didOpen: () => {

                Swal.showLoading();
            }
        });

        try {

            await fetch(
                '/saved-filter/save',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type':
                            'application/json',
                    },
                    body: JSON.stringify({
                        name:
                            result.value,

                        type:
                            this.typeValue,

                        filters,
                    }),
                }
            );

            await this.loadFilters();

            Swal.fire({

                icon: 'success',

                title:
                    'Filtro guardado',

                timer: 1500,

                showConfirmButton:
                    false,
            });

        } catch {

            Swal.fire({

                icon: 'error',

                title:
                    'Error',

                text:
                    'No se pudo guardar',
            });
        }
    }

    /*
     * ========================================
     * DELETE FILTER
     * ========================================
     */
    async delete(): Promise<void> {

        const id = Number(
            this.selectTarget.value
        );

        if (!id) {

            Swal.fire({

                icon: 'warning',

                title:
                    'Selecciona un filtro',
            });

            return;
        }

        const confirmed =
            await Swal.fire({

                icon: 'warning',

                title:
                    'Eliminar filtro',

                text:
                    'Esta acción no se puede deshacer',

                showCancelButton:
                    true,

                confirmButtonText:
                    'Eliminar',

                cancelButtonText:
                    'Cancelar',
            });

        if (!confirmed.isConfirmed) {
            return;
        }

        Swal.fire({

            title:
                'Eliminando...',

            allowOutsideClick:
                false,

            didOpen: () => {

                Swal.showLoading();
            }
        });

        try {

            await fetch(
                `/saved-filter/${id}`,
                {
                    method: 'DELETE',
                }
            );

            this.selectTarget.value =
                '';

            await this.loadFilters();

            Swal.fire({

                icon: 'success',

                title:
                    'Filtro eliminado',

                timer: 1500,

                showConfirmButton:
                    false,
            });

        } catch {

            Swal.fire({

                icon: 'error',

                title:
                    'Error',

                text:
                    'No se pudo eliminar',
            });
        }
    }

    /*
     * ========================================
     * APPLY FILTER
     * ========================================
     */
    apply(): void {

        const id = Number(
            this.selectTarget.value
        );

        if (!id) {
            return;
        }

        const filter =
            this.savedFilters.find(
                item => item.id === id
            );

        if (!filter) {
            return;
        }

        this.fillFilters(
            filter.filters
        );
    }

    /*
     * ========================================
     * COLLECT FILTERS
     * ========================================
     */
    collectFilters(): Record<string, any> {

        const filters:
            Record<string, any> = {};

        const elements =
            this.element.querySelectorAll(
                '[data-saved-filter-field]'
            );

        elements.forEach(
            (element: Element) => {

                const field =
                    element as
                    HTMLInputElement |
                    HTMLSelectElement;

                /*
                 * RADIO
                 */
                if (
                    field instanceof
                    HTMLInputElement
                    &&
                    field.type ===
                    'radio'
                ) {

                    if (field.checked) {
                        filters[field.name] =
                            field.value;
                    }

                    return;
                }

                /*
                 * CHECKBOX
                 */
                if (
                    field instanceof
                    HTMLInputElement
                    &&
                    field.type ===
                    'checkbox'
                ) {

                    filters[field.name] =
                        field.checked;

                    return;
                }

                /*
                 * MULTIPLE SELECT
                 */
                if (
                    field instanceof
                    HTMLSelectElement
                    &&
                    field.multiple
                ) {

                    const tomSelect =
                        (field as any)
                            .tomselect;

                    /*
                     * TomSelect
                     */
                    if (tomSelect) {

                        filters[field.name] =
                            tomSelect.items.map(
                                (
                                    item: string
                                ) => {

                                    const option =
                                        tomSelect.options[
                                            item
                                        ];

                                    return {
                                        value:
                                            item,

                                        label:
                                            option?.text
                                            ??
                                            item,
                                    };
                                }
                            );

                        return;
                    }

                    /*
                     * Native select
                     */
                    filters[field.name] =
                        Array.from(
                            field.selectedOptions
                        ).map(
                            option => ({
                                value:
                                    option.value,

                                label:
                                    option.textContent
                                    ??
                                    option.value,
                            })
                        );

                    return;
                }

                /*
                 * DEFAULT
                 */
                filters[field.name] =
                    field.value;
            }
        );

        return filters;
    }

    /*
     * ========================================
     * FILL FILTERS
     * ========================================
     */
    fillFilters(
        filters: Record<string, any>
    ): void {

        const elements =
            this.element.querySelectorAll(
                '[data-saved-filter-field]'
            );

        elements.forEach(
            (element: Element) => {

                const field =
                    element as
                    HTMLInputElement |
                    HTMLSelectElement;

                const value =
                    filters[field.name];

                if (
                    value === undefined
                ) {

                    return;
                }

                /*
                 * RADIO
                 */
                if (
                    field instanceof
                    HTMLInputElement
                    &&
                    field.type ===
                    'radio'
                ) {

                    field.checked =
                        field.value === value;

                    return;
                }

                /*
                 * CHECKBOX
                 */
                if (
                    field instanceof
                    HTMLInputElement
                    &&
                    field.type ===
                    'checkbox'
                ) {

                    field.checked =
                        Boolean(value);

                    field.dispatchEvent(
                        new Event(
                            'change',
                            {
                                bubbles: true,
                            }
                        )
                    );

                    return;
                }

                /*
                 * MULTIPLE SELECT
                 */
                if (
                    field instanceof
                    HTMLSelectElement
                    &&
                    field.multiple
                ) {

                    const items:
                        SavedOption[] =
                        Array.isArray(value)
                            ? value
                            : [value];

                    const values =
                        items.map(
                            item =>
                                item.value
                        );

                    const tomSelect =
                        (field as any)
                            .tomselect;

                    /*
                     * TomSelect
                     */
                    if (tomSelect) {

                        tomSelect.clear();

                        items.forEach(
                            item => {

                                tomSelect.addOption({

                                    value:
                                        item.value,

                                    text:
                                        item.label,
                                });
                            }
                        );

                        tomSelect.setValue(
                            values
                        );

                        tomSelect.refreshItems();

                    } else {

                        /*
                         * Native select
                         */
                        Array.from(
                            field.options
                        ).forEach(
                            option => {

                                option.selected =
                                    values.includes(
                                        option.value
                                    );
                            }
                        );
                    }

                    field.dispatchEvent(
                        new Event(
                            'change',
                            {
                                bubbles: true,
                            }
                        )
                    );

                    return;
                }

                /*
                 * DEFAULT
                 */
                field.value = value;

                field.dispatchEvent(
                    new Event(
                        'change',
                        {
                            bubbles: true,
                        }
                    )
                );
            }
        );

        /*
         * APPLY FILTERS
         */
        setTimeout(() => {

            const applyButton =
                document.getElementById(
                    'apply-filters'
                );

            applyButton?.click();

        }, 200);
    }
}
